import { ProfileIcon } from 'assets/icons/ProfileIcon';
import { MyProfileForm } from 'components/Form/MyProfile/MyProfileForm';
import {
  DEFAULT_VALUES,
  ProfileInformation,
  ProfileInformationFormKeys,
} from 'components/Form/MyProfile/useMyProfileFormik';
import { useDeviceCategory } from 'components/Layout/useDeviceCategory';
import { SidePanel } from 'components/SidePanel/SidePanel';
import { type JSX, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setIsError } from 'redux/ApiResponseStatusGlobalDisplay/slice';
import { getProfileDetails, getUserMetadataId } from 'redux/User/selectors';
import { getPasswordInfo, updateProfile } from 'services/api/myProfile/client';
import { PasswordInfo } from 'services/api/myProfile/types';
import { HttpError } from 'services/api/types';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';

type Props = {
  isOpen: boolean;
  onAbort: () => void;
  onProfileUpdateSuccess: () => void;
};

export const MyProfileSidePanel = ({
  isOpen,
  onAbort,
  onProfileUpdateSuccess,
}: Props): JSX.Element => {
  const MyProfileIntl = useIntlByKey('my-profile-form');
  const dispatch = useDispatch();
  const userMetadataId = useSelector(getUserMetadataId);
  const userInformation = useSelector(getProfileDetails);
  const [passwordInfo, setPasswordInfo] = useState<PasswordInfo | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let isActive = true;
    getPasswordInfo()
      .then((info) => {
        if (isActive) setPasswordInfo(info);
      })
      .catch(() => {
        if (isActive) setPasswordInfo(null);
      });
    return () => {
      isActive = false;
    };
  }, [isOpen]);

  const onSubmit = async (profileInfo: ProfileInformation) => {
    if (userMetadataId) {
      await updateProfile({ ...profileInfo, userId: userMetadataId });
      onProfileUpdateSuccess();
    }
  };

  const onSubmitError = (_httpError: HttpError) => {
    dispatch(setIsError(true));
  };

  const { isMobile, isTablet } = useDeviceCategory();
  return (
    <>
      <SidePanel
        title={MyProfileIntl('title')}
        titleIcon={<ProfileIcon />}
        isOpen={isOpen}
        onClose={onAbort}
        widthInPercent={isMobile ? 100 : isTablet ? 60 : undefined}
        isMobile={isMobile || isTablet}
      >
        <MyProfileForm
          onSubmit={onSubmit}
          onSubmitError={onSubmitError}
          onAbort={onAbort}
          passwordInfo={passwordInfo}
          initialFormValues={{
            ...DEFAULT_VALUES,
            [ProfileInformationFormKeys.USER_FIRST_NAME]: userInformation?.firstName ?? '',
            [ProfileInformationFormKeys.USER_LAST_NAME]: userInformation?.lastName ?? '',
            [ProfileInformationFormKeys.USER_PHONE_NUMBER]: userInformation?.phoneNumber ?? '',
          }}
        />
      </SidePanel>
    </>
  );
};
